import React from 'react'

import PlanItemDetailsModal, { PlanItemState } from './PlanItemDetailsModal'
import {
  UpdatePlanItemInput,
  useDeletePlanItemMutation,
  usePlanItemQuery,
  useUpdatePlanItemMutation
} from '../../../../generated/graphql'
import { getPlanItemAlerts } from './utils'
import { updateDeletePlanItemCache, updateUpdatePlanItemCache } from '../../../../lib/cacheUtils'
import useAppSettings from '../../../../lib/hooks/useAppSettings'
import useMixpanelEvent from '../../../../lib/hooks/useMixpanelEvent'
import useModal from '../../../../lib/hooks/useModal'
import useNotification from '../../../../lib/hooks/useNotification'

type PlanItemDetailsModalWrapperProps = {
  /** Campus used to filter courses */
  campus?: string
  /** Whether the delete button should be disabled */
  disableDelete?: boolean
  /** Whether the group field should be disabled */
  disableGroupChange?: boolean
  /** ID of Item */
  id: string
  /** Determines visibility of modal */
  isOpen: boolean
  /** If the plan is a template */
  isTemplate: boolean
  /** ID of Plan */
  planId: string
  /** Whether all of the inputs and buttons should be disabled */
  readOnly: boolean
}

const defaultProps: Pick<
  PlanItemDetailsModalWrapperProps,
  'campus' | 'disableDelete' | 'disableGroupChange'
> = {
  campus: undefined,
  disableDelete: false,
  disableGroupChange: false
}

const PlanItemDetailsModalWrapper = ({
  campus,
  disableDelete,
  disableGroupChange,
  id,
  isOpen,
  isTemplate,
  planId,
  readOnly
}: PlanItemDetailsModalWrapperProps) => {
  const { text } = useAppSettings()
  const { showErrorNotif } = useNotification()
  const courseLabel = text.course
  const advisorLabel = text.advisor
  const termLabel = text.term
  const planItemQuery = usePlanItemQuery({
    variables: { id, planId },
    onError: () => {
      showErrorNotif('An error occurred. Please refresh the page.')
    }
  })
  const [updatePlanItem] = useUpdatePlanItemMutation()
  const [deletePlanItem] = useDeletePlanItemMutation()
  const { hideModal } = useModal()
  const { addMixpanelEvent } = useMixpanelEvent()

  const planItem = planItemQuery.data?.planItem
  const plan = planItemQuery.data?.plan
  const planGroups = plan?.groups || []
  const currentGroup = planGroups.find((g) => g.id === planItem?.groupId)
  const itemAlerts = planItemQuery.data?.planAlerts || []
  const alerts = getPlanItemAlerts(
    itemAlerts,
    planItem,
    currentGroup,
    courseLabel,
    advisorLabel,
    termLabel
  )

  const onCancel = () => {
    hideModal()
  }

  const onDelete = () => {
    hideModal()
    deletePlanItem({
      variables: { input: id },
      update: (cache, { data }) => updateDeletePlanItemCache(cache, planItem, data),
      optimisticResponse: {
        __typename: 'Mutation',
        deletePlanItem: {
          __typename: 'ResponseStatus',
          id,
          message: '',
          success: true
        }
      }
    })
  }

  const onSave = (planItemState: PlanItemState) => {
    const {
      group,
      locked,
      nonCourseCreditsMax,
      nonCourseCreditsMin,
      nonCourseTitle,
      required,
      selectedCourse,
      title
    } = planItemState
    // if we're moving the group, then append item to end of new group
    const sequence =
      group?.id === planItem?.groupId ? planItem?.displaySequence : (group?.items.length || 0) + 1
    const displaySequence = sequence || 0

    // Title is required for a non-course. Title can be edited for non-course items
    // Minimum credits must be provided if max credits are populated
    // Checking the type and the value due to the complexity of handling undefined, NaN, and 0
    const minCreditsPopulated = typeof nonCourseCreditsMin === 'number' && nonCourseCreditsMin >= 0
    const maxCreditsPopulated = typeof nonCourseCreditsMax === 'number' && nonCourseCreditsMax >= 0
    const validMinCredits = !maxCreditsPopulated || minCreditsPopulated

    // Map placeholders from CatalogSearchResultFragment to PlaceholderCourseInput
    const placeholderCourses = planItemState.isCoursePlaceholder
      ? planItemState.selectedCoursePlaceholders?.map((course) => ({
          courseNumber: course.courseNumber,
          creditsMax: course.creditsMax || 0,
          creditsMin: course.creditsMin || 0,
          description: course.description || '',
          externalCourseId: course.externalCourseId || '',
          subjectId: course.subjectId || '',
          title: course.title || ''
        }))
      : null

    if ((title || nonCourseTitle) && validMinCredits) {
      // If there is a selected course, save both placeholder and real course info
      const input: UpdatePlanItemInput = selectedCourse
        ? {
            courseNumber: selectedCourse.courseNumber,
            creditsMax: selectedCourse.creditsMax,
            creditsMin: selectedCourse.creditsMin,
            description: selectedCourse.description,
            displaySequence,
            externalCourseId: selectedCourse.externalCourseId,
            groupId: group?.id,
            id,
            isCoursePlaceholder: planItemState.isCoursePlaceholder,
            locked,
            nonCourseCreditsMax:
              planItemState.nonCourseCreditsMax || planItemState.nonCourseCreditsMin,
            nonCourseCreditsMin: planItemState.nonCourseCreditsMin,
            nonCourseDescription: planItemState.nonCourseDescription,
            nonCourseTitle: planItemState.nonCourseTitle,
            placeholderCourses,
            required,
            subjectId: selectedCourse.subjectId,
            title: selectedCourse.title
          }
        : {
            courseNumber: '',
            creditsMax: null,
            creditsMin: null,
            description: '',
            displaySequence,
            externalCourseId: '',
            groupId: group?.id,
            id,
            isCoursePlaceholder: planItemState.isCoursePlaceholder,
            nonCourseCreditsMax:
              planItemState.nonCourseCreditsMax || planItemState.nonCourseCreditsMin,
            nonCourseCreditsMin: planItemState.nonCourseCreditsMin,
            nonCourseDescription: planItemState.nonCourseDescription,
            nonCourseTitle: planItemState.nonCourseTitle,
            locked,
            required,
            placeholderCourses,
            subjectId: '',
            title: ''
          }
      const updatePlanItemData = selectedCourse
        ? {
            availability: planItem?.availability,
            courseNumber: selectedCourse.courseNumber,
            creditsMax: selectedCourse.creditsMax,
            creditsMin: selectedCourse.creditsMin,
            description: selectedCourse.description,
            displaySequence,
            externalCourseId: selectedCourse.externalCourseId,
            groupId: group?.id,
            id,
            inProgress: planItemState.inProgress,
            isActive: planItem?.isActive || true,
            isCoursePlaceholder: planItemState.isCoursePlaceholder,
            locked: planItemState.locked,
            nonCourseCreditsMax:
              planItemState.nonCourseCreditsMax || planItemState.nonCourseCreditsMin,
            nonCourseCreditsMin: planItemState.nonCourseCreditsMin,
            nonCourseDescription: planItemState.nonCourseDescription,
            nonCourseTitle: planItemState.nonCourseTitle,
            placeholderCourses,
            planId,
            required: planItemState.required,
            requirementTitles: planItem?.requirementTitles || [],
            requisiteDescription: selectedCourse.requisiteDescription || '',
            requisiteDescriptionLong: selectedCourse.requisiteDescriptionLong || '',
            showRequisiteAlert: planItem?.showRequisiteAlert,
            subjectId: selectedCourse.subjectId,
            title: selectedCourse.title,
            typicallyOffered: planItem?.typicallyOffered || '',
            usedToMeetRequirement: planItemState.usedToMeetRequirement && !planItemState.inProgress
          }
        : {
            availability: planItem?.availability,
            courseNumber: null,
            creditsMax: null,
            creditsMin: null,
            description: null,
            displaySequence,
            externalCourseId: null,
            groupId: group?.id,
            id,
            inProgress: planItemState.inProgress,
            isActive: planItem?.isActive || true,
            isCoursePlaceholder: planItemState.isCoursePlaceholder,
            locked: planItemState.locked,
            nonCourseCreditsMax:
              planItemState.nonCourseCreditsMax || planItemState.nonCourseCreditsMin,
            nonCourseCreditsMin: planItemState.nonCourseCreditsMin,
            nonCourseDescription: planItemState.nonCourseDescription,
            nonCourseTitle: planItemState.nonCourseTitle,
            placeholderCourses,
            planId,
            required: planItemState.required,
            requirementTitles: planItem?.requirementTitles || [],
            requisiteDescription: planItem?.requisiteDescription,
            requisiteDescriptionLong: planItem?.requisiteDescriptionLong,
            showRequisiteAlert: planItem?.showRequisiteAlert,
            subjectId: null,
            title: planItemState.title,
            typicallyOffered: '',
            usedToMeetRequirement: planItemState.usedToMeetRequirement && !planItemState.inProgress
          }

      hideModal()
      updatePlanItem({
        variables: { input },
        update: (cache, { data }) => {
          updateUpdatePlanItemCache(cache, data, planItem, plan?.ownerAppUserId)
        },
        optimisticResponse: {
          __typename: 'Mutation',
          updatePlanItem: {
            __typename: 'PlanItem',
            ...updatePlanItemData
          }
        }
      })

      if (!isTemplate) {
        // Skipping destructuring here to make sure we're comparing the right things
        if (!planItem?.isCoursePlaceholder && planItemState.isCoursePlaceholder) {
          addMixpanelEvent('Student Profile > Plan: Designate Course Placeholder')
        }

        if (!planItem?.locked && planItemState.locked) {
          addMixpanelEvent('Student Profile > Plan: Lock Item on Plan')
        }

        if (!planItem?.required && planItemState.required) {
          addMixpanelEvent('Student Profile > Plan: Mark item Required on Plan')
        }
      }
    }
  }

  return planItem ? (
    <PlanItemDetailsModal
      alerts={alerts}
      campus={campus}
      disableDelete={disableDelete}
      disableGroupChange={disableGroupChange}
      isOpen={isOpen}
      onCancel={onCancel}
      onDelete={onDelete}
      onSave={onSave}
      planGroups={planGroups}
      planItem={planItem}
      readOnly={readOnly}
    />
  ) : null
}

PlanItemDetailsModalWrapper.defaultProps = defaultProps

export default PlanItemDetailsModalWrapper
